import { useEffect, useMemo, useState } from 'react';
import Helmet from '../lib/Helmet';
import { formatINR } from '../lib/quote';

// Route: /admin/stock. Team only, not linked from anywhere, noindex.
//
// Log in with a 6-digit code sent to a team email, then record units sold in
// the showroom or on the phone, stock that came in, or a fresh count. The
// server (api/stock-admin.js) keeps the list of who may log in and does all
// the checking; this page only shows what it is given.

const API = '/api/stock-admin';

const input = (bad) =>
  `mt-1 w-full rounded-lg border px-3 py-3 text-[15px] outline-none transition ${
    bad ? 'border-red-400 bg-red-50/40' : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
  }`;
const primary = 'w-full rounded-lg bg-blue-600 px-5 py-3.5 text-base font-medium text-white transition hover:bg-blue-700 disabled:opacity-60';

async function call(body) {
  const res = await fetch(API, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  return { status: res.status, data };
}

const when = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return ''; }
};

const KIND = {
  sold: { verb: 'Sold', button: (n) => `Record ${n} sold`, max: 50, min: 1, start: 1 },
  in: { verb: 'Stock in', button: (n) => `Add ${n} to stock`, max: 50, min: 1, start: 1 },
  count: { verb: 'Set count', button: (n) => `Set count to ${n}`, max: 999, min: 0, start: 0 },
};

function Login({ onIn }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(''); setMsg('');
    try {
      const { status, data } = await call({ action: 'send-code', email });
      if (status === 200) { setStep('code'); setMsg(data.message || 'Check your email for the code.'); }
      else setErr(data.error || 'Could not send a code. Try again.');
    } catch { setErr('No connection. Try again.'); }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const { status, data } = await call({ action: 'verify', email, code });
      if (status === 200 && data.ok) onIn(data);
      else setErr(data.error || 'Could not log in. Try again.');
    } catch { setErr('No connection. Try again.'); }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-sm">
      <h1 className="text-2xl font-bold text-gray-900">Team stock</h1>
      <p className="mt-1 text-sm text-gray-600">For the Citi Cool team. Log in with the code we email you.</p>

      {step === 'email' ? (
        <form onSubmit={send} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-[13px] font-medium text-gray-700">Work email</span>
            <input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className={input(Boolean(err))} />
          </label>
          {err && <p className="text-[13px] text-red-600">{err}</p>}
          <button type="submit" disabled={busy || !email} className={primary}>{busy ? 'Sending…' : 'Send code'}</button>
        </form>
      ) : (
        <form onSubmit={verify} className="mt-6 space-y-4">
          {msg && <p className="rounded-lg bg-blue-50 px-3 py-2.5 text-[13px] text-blue-900">{msg}</p>}
          <label className="block">
            <span className="text-[13px] font-medium text-gray-700">6-digit code</span>
            <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className={`${input(Boolean(err))} tracking-[0.4em] text-center text-xl font-semibold`} />
          </label>
          {err && <p className="text-[13px] text-red-600">{err}</p>}
          <button type="submit" disabled={busy || code.length !== 6} className={primary}>{busy ? 'Checking…' : 'Log in'}</button>
          <div className="flex justify-between text-[13px]">
            <button type="button" onClick={() => { setStep('email'); setCode(''); setErr(''); }} className="font-medium text-gray-600">Change email</button>
            <button type="button" onClick={send} disabled={busy} className="font-medium text-blue-700">Send a new code</button>
          </div>
        </form>
      )}
    </div>
  );
}

function Status({ item }) {
  if (item.live == null) return <span className="text-[12px] font-semibold text-gray-500">On order</span>;
  if (item.live === 0) return <span className="rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-semibold text-white">Sold out</span>;
  return null;
}

function Row({ item, open, onOpen, onDone }) {
  const [kind, setKind] = useState('sold');
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const pick = (k) => {
    setKind(k); setErr('');
    setQty(k === 'count' ? (item.live ?? 0) : 1);
  };

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const { status, data } = await call({ action: kind, modelId: item.modelId, kit: item.kit, qty, note });
      if (status === 200 && data.ok) { onDone(data, { item, kind, qty }); setNote(''); }
      else if (status === 401) onDone(null);
      else setErr(data.error || 'Could not save. Try again.');
    } catch { setErr('No connection. Try again.'); }
    setBusy(false);
  };

  const k = KIND[kind];
  const canSell = (item.live || 0) > 0;
  const parts = [
    item.sheet != null ? `Sheet ${item.sheet}` : 'Not on the sheet',
    item.online ? `online −${item.online}` : '',
    item.showroom ? `showroom −${item.showroom}` : '',
  ].filter(Boolean).join(' · ');

  return (
    <li className="rounded-xl border border-gray-200 bg-white">
      <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 p-3 text-left" aria-expanded={open}>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[15px] font-bold text-gray-900">{item.short}</span>
            <Status item={item} />
            {item.onSite && item.live > 0 && <span className="text-[11px] font-medium text-blue-700">On the website</span>}
          </p>
          <p className="mt-0.5 text-[12px] text-gray-600">{item.line}</p>
          <p className="mt-0.5 text-[12px] text-gray-500">{formatINR(item.price)}{item.code !== item.short ? ` · ${item.code}` : ''}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className={`text-2xl font-bold ${item.live > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{item.live ?? '–'}</p>
          <p className="text-[10px] uppercase tracking-wide text-gray-500">in stock</p>
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 p-3">
          <p className="text-[12px] text-gray-500">{parts}</p>
          <div className="mt-3 grid grid-cols-3 gap-1.5" role="tablist">
            {Object.entries(KIND).map(([key, v]) => (
              <button key={key} type="button" role="tab" aria-selected={kind === key} onClick={() => pick(key)}
                disabled={key === 'sold' && !canSell}
                className={`rounded-lg border px-2 py-2 text-[13px] font-semibold transition disabled:opacity-40 ${
                  kind === key ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300 text-gray-700'
                }`}>
                {v.verb}
              </button>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-[13px] font-medium text-gray-700">{kind === 'count' ? 'Units in hand now' : 'Units'}</span>
            <div className="flex items-center">
              <button type="button" aria-label="Less" onClick={() => setQty((n) => Math.max(k.min, n - 1))}
                className="h-11 w-11 rounded-l-lg border border-gray-300 text-lg text-gray-700">−</button>
              <input inputMode="numeric" value={qty} aria-label="Units"
                onChange={(e) => setQty(Math.min(k.max, Math.max(0, Number(e.target.value.replace(/\D/g, '')) || 0)))}
                className="h-11 w-14 border-y border-gray-300 text-center text-base font-semibold outline-none" />
              <button type="button" aria-label="More" onClick={() => setQty((n) => Math.min(kind === 'sold' ? Math.min(k.max, item.live || 0) : k.max, n + 1))}
                className="h-11 w-11 rounded-r-lg border border-gray-300 text-lg text-gray-700">+</button>
            </div>
          </div>

          <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 120))} placeholder="Note (optional), e.g. customer name or invoice no."
            className={`${input(false)} mt-3 py-2.5 text-[14px]`} />

          {err && <p className="mt-2 text-[13px] text-red-600">{err}</p>}
          <button type="button" onClick={save} disabled={busy || qty < k.min} className={`${primary} mt-3`}>
            {busy ? 'Saving…' : k.button(qty)}
          </button>
        </div>
      )}
    </li>
  );
}

const describe = (r) => {
  const unit = `${r.code}${r.kit === 'withKit' ? ' (with kit)' : ''}`;
  if (r.kind === 'sold') return `${r.qty} ${unit} sold`;
  if (r.kind === 'in') return `${r.qty} ${unit} came in`;
  return `${unit} counted: ${r.qty}`;
};

function Stock({ data, setData, onOut }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState('');
  const [showOrder, setShowOrder] = useState(false);
  const [notice, setNotice] = useState('');

  const groups = useMemo(() => {
    const term = q.trim().toUpperCase().replace(/\s+/g, '');
    const hit = (i) => !term || `${i.code}${i.short}${i.name}`.toUpperCase().replace(/\s+/g, '').includes(term);
    const list = data.items.filter(hit);
    // Daikin first, then old stock, each by model code.
    const byCode = (a, b) => (a.clearance - b.clearance) || a.short.localeCompare(b.short);
    return {
      stocked: list.filter((i) => i.live > 0).sort(byCode),
      soldOut: list.filter((i) => i.live === 0).sort(byCode),
      onOrder: list.filter((i) => i.live == null).sort(byCode),
    };
  }, [data.items, q]);

  const done = (next, change) => {
    if (!next) { onOut(); return; }
    setData(next);
    const after = next.items.find((i) => i.modelId === change.item.modelId && i.kit === change.item.kit);
    const what = change.kind === 'sold' ? `${change.qty} sold` : change.kind === 'in' ? `${change.qty} added` : `count set to ${change.qty}`;
    setNotice(`${change.item.short}: ${what}. ${after?.live ?? 0} in stock now.${
      next.rebuilding ? ' The website is updating; it takes about 2 minutes.' : ''
    }`);
    setOpen('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const logout = async () => {
    await call({ action: 'logout' }).catch(() => {});
    onOut();
  };

  const section = (title, items, hint) => (
    <section className="mt-6">
      <h2 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500">{title} · {items.length}</h2>
      {hint && <p className="mt-1 text-[12px] text-gray-500">{hint}</p>}
      <ul className="mt-2 space-y-2">
        {items.map((i) => {
          const key = `${i.modelId}:${i.kit}`;
          return <Row key={key} item={i} open={open === key} onOpen={() => setOpen(open === key ? '' : key)} onDone={done} />;
        })}
      </ul>
    </section>
  );

  const h = data.health || {};
  const warn = [
    h.onlineSales !== 'yes' && `Online sales are not being counted: ${h.onlineSales}.`,
    h.stockPage !== 'yes' && h.onlineSales === 'yes' && `Changes made here are not being counted yet: ${h.stockPage}.`,
    h.siteUpdates !== 'yes' && 'The website does not update by itself after a change here. Claude can switch this on.',
  ].filter(Boolean);

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team stock</h1>
          <p className="mt-0.5 text-[12px] text-gray-500">{data.email} · <button type="button" onClick={logout} className="font-medium text-blue-700">Log out</button></p>
        </div>
      </div>

      {notice && <p className="mt-4 rounded-lg bg-green-50 px-3 py-2.5 text-[13px] font-medium text-green-900">{notice}</p>}
      {warn.length > 0 && (
        <div className="mt-4 space-y-1 rounded-lg bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900">
          {warn.map((w) => <p key={w}>{w}</p>)}
        </div>
      )}

      <p className="mt-4 text-[12px] leading-relaxed text-gray-500">
        Every unit sold in the showroom or on the phone goes here, so the website never sells one we no longer have. Online orders come off by themselves. Tap an AC to record a sale, stock that came in, or a fresh count.
      </p>

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a model, e.g. FTHT60"
        className={`${input(false)} mt-3`} />

      {section('In stock', groups.stocked)}
      {groups.soldOut.length > 0 && section('Sold out', groups.soldOut, 'Shown as sold out on the website. Use Stock in when new units arrive.')}
      <section className="mt-6">
        <button type="button" onClick={() => setShowOrder((s) => !s)} className="text-[13px] font-semibold uppercase tracking-wide text-gray-500">
          On order · {groups.onOrder.length} {showOrder ? '▴' : '▾'}
        </button>
        {showOrder && (
          <ul className="mt-2 space-y-2">
            {groups.onOrder.map((i) => {
              const key = `${i.modelId}:${i.kit}`;
              return <Row key={key} item={i} open={open === key} onOpen={() => setOpen(open === key ? '' : key)} onDone={done} />;
            })}
          </ul>
        )}
      </section>

      <section className="mt-8">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500">Recent changes</h2>
        {data.recent.length === 0 ? (
          <p className="mt-2 text-[13px] text-gray-500">Nothing recorded here yet.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
            {data.recent.map((r) => (
              <li key={r.id} className="px-3 py-2.5">
                <p className="text-[13px] font-medium text-gray-900">{describe(r)}</p>
                <p className="text-[11px] text-gray-500">{when(r.at)} · {r.by}{r.note ? ` · ${r.note}` : ''}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default function StockAdmin() {
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { status, data: d } = await call();
        if (cancelled) return;
        if (status === 200 && d.ok) { setData(d); setPhase('stock'); }
        else if (status === 401) setPhase('login');
        else { setProblem(d.error || 'The stock page could not load.'); setPhase('problem'); }
      } catch {
        if (!cancelled) { setProblem('No connection. Reload the page.'); setPhase('problem'); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <main className="mx-auto max-w-xl px-4 pt-24 pb-16 lg:pt-28">
      <Helmet>
        <title>Team stock | Citi Cool</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {phase === 'loading' && <p className="py-16 text-center text-sm text-gray-500">Loading…</p>}
      {phase === 'problem' && <p className="rounded-lg bg-amber-50 px-3 py-3 text-[14px] text-amber-900">{problem}</p>}
      {phase === 'login' && <Login onIn={(d) => { setData(d); setPhase('stock'); }} />}
      {phase === 'stock' && data && (
        <Stock data={data} setData={setData} onOut={() => { setData(null); setPhase('login'); }} />
      )}
    </main>
  );
}
